import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { shortDay } from '@/lib/format';
import { dayHeading, stopMark } from './artifact-meta';
import { caption } from './parts';

type Calendar = Extract<WireArtifact, { kind: 'calendar' }>;
type Day = Calendar['days'][number];
type Entry = Day['entries'][number];

/**
 * The stops the provider dated, in day buckets. Only days the records name
 * appear: a day nothing happened on is not invented, and no time is shifted
 * into a zone the provider never stated.
 */
export function CalendarArtifact({ artifact }: { artifact: Calendar }): JSX.Element {
  const stops = artifact.days.reduce((sum, d) => sum + d.entries.length, 0);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className={caption}>
          {shortDay(artifact.from)} – {shortDay(artifact.to)}
        </span>
        <span className="text-mute font-mono text-[10.5px] tabular-nums">
          {stops} {stops === 1 ? 'stop' : 'stops'}
        </span>
      </div>
      {artifact.days.map((day) => (
        <CalendarDay key={day.day} day={day} />
      ))}
    </div>
  );
}

function CalendarDay({ day }: { day: Day }): JSX.Element {
  return (
    <div className="border-line bg-surf rounded-xl border px-3.5 py-3">
      <div className="border-line flex items-baseline justify-between gap-3 border-b pb-2">
        <span className="text-[12.5px] font-medium">{dayHeading(day.day)}</span>
        <span className="text-mute font-mono text-[10px] tabular-nums">{day.entries.length}</span>
      </div>
      <div className="flex flex-col">
        {day.entries.map((entry, i) => (
          <CalendarEntry key={i} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function CalendarEntry({ entry }: { entry: Entry }): JSX.Element {
  return (
    <div className="border-line flex items-start gap-2.5 border-b py-2 last:border-b-0 last:pb-0">
      <span className="bg-surf2 text-acc mt-px grid h-6 w-[34px] flex-none place-items-center rounded-md font-mono text-[10px] font-medium">
        {stopMark(entry.kind)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-[12px] font-medium">
          #{entry.reference.replace(/^#/, '')}
        </div>
        {entry.place !== undefined && (
          <div className="text-mute mt-0.5 text-[11.5px] [overflow-wrap:anywhere]">
            {entry.place}
          </div>
        )}
      </div>
      {entry.at !== undefined && (
        <span className="text-mute max-w-[96px] flex-none truncate text-right font-mono text-[10px]">
          {entry.at}
        </span>
      )}
    </div>
  );
}
